
import apiFactory from '@api/user'
import { Module } from 'vuex'
import settings from '@storeModules/settings'
import activities from '@storeModules/activities'
import content from '@storeModules/content'
import { Store, User } from '@/types'
import contacts from '@storeModules/contacts'
import cookies from '@/utils/cookies'
import eventTracker from '@/utils/eventTracker'
import moment, { Moment } from 'moment-timezone'
import { AxiosRequestConfig } from 'axios'

const api = apiFactory()

const tokenRepairShop = {
  flag: false,
  barrier: Promise.resolve(null) as Promise<User.API.ReqRes.Login.Response | null>
}

const storeModule: Module<User.ModuleState, Store.RootState> = {
  namespaced: true,
  modules: {
    settings,
    activities,
    content,
    contacts
  },
  state: {
    access_token: cookies.getCookie('access_token'),
    refresh_token: cookies.getCookie('refresh_token'),
    acceptedAgreements: [],
    loading: false,
    reportURL: ''
  },
  mutations: {
    SET_TOKENS: (state, { access_token, refresh_token }) => {
      state.access_token = access_token
      state.refresh_token = refresh_token
      cookies.setCookie('access_token', access_token)
      cookies.setCookie('refresh_token', refresh_token)
    },
    RETIRE_TOKEN: (state) => {
      state.access_token = ''
      console.log('Token retired!')
    },
    SET_LOADING: (state, value) => {
      state.loading = value
    },
    SET_PATIENT: (state, me: User.API.ReqRes.Me.Response) => {
      state.userId = me.userId
      state.me = me
    },
    SET_CREDENTIALS: (state, credentials: User.API.ReqRes.Login.Credentials) => {
      state.credentials = credentials
    },
    SET_ACCEPTED_AGREEMENTS: (state, acceptedAgreements: User.AcceptedAgreements) => {
      state.acceptedAgreements = acceptedAgreements
    },
    SET_REPORT_URL: (state, reportURL: string) => {
      if (state.reportURL) {
        window.URL.revokeObjectURL(state.reportURL)
      }
      state.reportURL = reportURL
    },
    LOGOUT: (state) => {
      delete state.access_token
      delete state.refresh_token
      cookies.removeCookie('access_token')
      cookies.removeCookie('refresh_token')
    }
  },
  actions: {
    SetCredentials ({ commit }, credentials: User.API.ReqRes.Login.Credentials) {
      commit('SET_CREDENTIALS', credentials)
    },
    SetLoading ({ commit }, value: boolean) {
      commit('SET_LOADING', value)
    },
    async Login ({ commit, getters }) {
      commit('SET_TOKENS', await api.login(getters.credentials))
    },
    async Me ({ commit }) {
      const me = await api.me()
      commit('SET_PATIENT', me)
      eventTracker.identify(me.userId, me)
      eventTracker.track('Account Success', { accountId: me.userId })
    },
    async Activate ({ state, commit }) {
      if (state.me && !state.me.activationTime) {
        commit('SET_TOKENS', await api.activate(state.refresh_token))
        eventTracker.track('Account Activated', { accountActivatedVia: 'MagicLink' })
      }
    },
    async GetInfo ({ dispatch, commit }) {
      commit('SET_LOADING', true)
      await Promise.all([
        dispatch('Me'),
        dispatch('settings/Read'),
        dispatch('GetAcceptedAgreements'),
        dispatch('activities/GetList')
      ])
      await Promise.all([
        dispatch('content/GetCategories')
      ])
      Promise.all([
        dispatch('content/GetBookmarks'),
        dispatch('GetReportURL').catch(console.error)
      ]).then(
        () => {
          commit('SET_LOADING', false)
        }
      )
    },
    async GetReportURL ({ state, commit }) {
      if (state.me?.trialStartDate && state.me?.trialEndDate) {
        commit('SET_REPORT_URL', await api.getReportURL())
      }
    },
    async GetAcceptedAgreements ({ commit }) {
      commit('SET_ACCEPTED_AGREEMENTS', await api.getAcceptedAgreements())
    },
    async AcceptAgreement ({ dispatch }, doc: User.AgreementTypes) {
      await api.acceptAgreement(doc)
      await dispatch('GetAcceptedAgreements')
    },
    Logout ({ commit }) {
      commit('LOGOUT')
    },
    async QueueFailedRequest ({ commit, state }, config: AxiosRequestConfig) {
      if (!tokenRepairShop.flag) {
        tokenRepairShop.flag = true
        tokenRepairShop.barrier = new Promise(
          (resolve) => {
            console.warn(`Token refresh procedure triggered by failure of ${config.url} request`)
            api.refreshToken(state.refresh_token)
              .then(data => {
                commit('SET_TOKENS', data)
                tokenRepairShop.flag = false
                resolve()
              })
              .catch(() => {
                commit('LOGOUT')
                window.location.reload()
              })
          }
        )
      }
      await tokenRepairShop.barrier
      return api.retryFailedRequest({
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${state.access_token}`
        }
      })
    },
    ErrorHandlingDemo (_, value: number) {
      return new Promise(
        (resolve, reject) => {
          const rand = Math.floor(100 * Math.random())
          if (rand < 50) {
            resolve(value)
          }
          else {
            reject(
              new Error(`Couldn't process value ${value}`)
            )
          }
        }
      )
    }
  },
  getters: {
    loading: state => state.loading,
    access_token: state => state.access_token,
    refresh_token: state => state.refresh_token,
    userId: state => state.userId,
    credentials: state => state.credentials,
    byKey: state => (key: keyof User.ModuleState) => state[key],
    acceptedAgreements: state => state.acceptedAgreements,
    me: state => state.me,
    reportURL: state => state.reportURL,
    trialTime: state => {
      if (state.me) {
        const day0 = moment(state.me.trialStartDateLocal || state.me.trialStartDate).startOf('day')
        const moments = {
          day0,
          start:
            moment(day0)
              .startOf('day')
              .add(1, 'day'),
          end:
            moment(state.me.trialEndDateLocal || state.me.trialEndDate)
              .startOf('day'),
          today:
            moment()
              .startOf('day')
        }
        const formats: User.DateFormats[] = [
          'MMMM D, YYYY',
          'MMMM D',
          'YYYY-MM-DD'
        ]
        const formatReducer = (
          m: Moment
        ) => (
          acc: Record<User.DateFormats, string>,
          f: string
        ) => {
          return {
            ...acc,
            [f]: m.format(f)
          }
        }
        const start = formats.reduce(
          formatReducer(moments.start),
          {} as Record<User.DateFormats, string>
        )
        const end = formats.reduce(
          formatReducer(moments.end),
          {} as Record<User.DateFormats, string>
        )
        const isYearSame = moments.start.format('YYYY') === moments.end.format('YYYY')
        const is = (m: Moment = moments.today) => ({
          trialStarted: m.isSameOrAfter(moments.start),
          trialAboutToEnd: m.isSameOrAfter(moments.end.clone().subtract(1, 'days')),
          trialLastDay: m.isSame(moments.end)
        })
        const dayRange = isYearSame
          ? `${start['MMMM D']} - ${end['MMMM D, YYYY']}`
          : `${start['MMMM D, YYYY']} - ${end['MMMM D, YYYY']}`
        return {
          moments,
          start,
          end,
          is,
          dayRange
        }
      }
      return null
    }
  }
}

export default storeModule
